import {Tooltip} from '@jobseeker/lotus/elements';
import {Grid} from '@jobseeker/lotus/foundations';
import {Box, Typography, useTheme} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import Link from 'next/link';
import {useEffect, useState} from 'react';
import {Dot} from '@jobseeker/lotus/icons';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {Avatar} from '@jobseeker/lotus/components';

export type JobAndRecruiterInfoProps = {
  recruiterId?: number;
  jobInfo: {
    title?: string;
    minExp?: number;
    maxExp?: number;
    locations?: string;
    confidential?: number;
  };
  recruiterInfo: {
    name?: string;
    profileUrl?: string;
    imgUrl?: string;
    organization?: string;
    designation?: string;
  };
};

export const JobAndRecruiterDetail = (props: JobAndRecruiterInfoProps) => {
  let {
    recruiterId = 0,
    jobInfo: {
      title = '',
      minExp = 0,
      maxExp = 0,
      locations = '',
      confidential = 0,
    },
    recruiterInfo: {
      name = '',
      profileUrl = '',
      imgUrl = '',
      organization = '',
      designation = '',
    },
  } = props;
  profileUrl =
    profileUrl ||
    `${process.env.NEXT_PUBLIC_LOAD_CONFIG}/r/${recruiterId}-${cleanUrl(
      name
    )}.html`;

  const theme = useTheme();
  const isAboveLarge = useIsLargeDevice(1024);
  const titleWithoutWorkExp = title.split(' (')[0];
  const titleThresholdLength = isAboveLarge ? 160 : 97;
  const truncatedTitle =
    titleWithoutWorkExp.length > titleThresholdLength
      ? titleWithoutWorkExp.slice(0, titleThresholdLength + 1) + '...'
      : titleWithoutWorkExp;
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setShowTooltip(titleWithoutWorkExp.length > titleThresholdLength);
  }, [titleWithoutWorkExp, titleThresholdLength]);

  return (
    <Box maxWidth={'525px'}>
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: {xxs: 12},
          lineHeight: '16px',
          marginBottom: {xxs: 0.5, md: 0.25},
        }}
      >
        {showTooltip ? (
          <Tooltip
            placement="bottom-end"
            arrow
            title={titleWithoutWorkExp}
            enterTouchDelay={0}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [480, 0],
                    },
                  },
                ],
              },
            }}
          >
            {truncatedTitle}
          </Tooltip>
        ) : (
          truncatedTitle
        )}
      </Typography>

      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 400,
          lineHeight: '14px',
          mb: 1,
          color: 'text.secondary',
          wordBreak: 'break-word',
        }}
      >
        {minExp}-{maxExp} yrs
        <Image
          src={Dot}
          height={4}
          width={4}
          alt="dot"
          style={{marginLeft: '8px', marginRight: '8px'}}
        />
        {locations}
      </Typography>

      <Grid
        container
        gap={{xxs: 1.75, md: 1.5}}
        mb={{xxs: 1, md: 0}}
        alignItems={'center'}
      >
        <Grid item p={0} m={0} borderRadius={1.5} lineHeight={0}>
          <Avatar
            onClick={() => {
              if (confidential === 0) {
                openUrl(profileUrl, '_blank');
              }
            }}
            imgUrl={confidential === 1 ? '' : imgUrl ?? ''}
            sx={{
              borderColor: '#49966E',
              color: theme.palette.primary.light,
            }}
          />
        </Grid>
        <Grid item>
          <Typography
            variant="body2"
            sx={{
              fontSize: {xxs: 10, md: 12},
              fontWeight: 500,
              lineHeight: {xxs: '12px', md: '18px'},
              color: '#333',
              marginBottom: '2px',
            }}
          >
            Posted by:{' '}
            {confidential === 1 ? (
              'Verified Recruiter'
            ) : (
              <Link
                style={{
                  color: '#49966E',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
                target="_blank"
                href={profileUrl}
              >
                {name}
              </Link>
            )}
          </Typography>
          {confidential === 0 && (
            <Typography
              fontSize={{xxs: 10, md: 12}}
              fontWeight={400}
              lineHeight={{xxs: '12px', md: '18px'}}
            >
              {designation} @ {organization}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default JobAndRecruiterDetail;
